import { Link } from 'react-router-dom';

import { ReactComponent as VideoCallIcon } from '@/assets/icons/video-call.svg';
import ctaBg from '@/assets/images/cta-bg.webp';
import { ROUTES } from '@/constants/routes';
import { DEPLOY_AI_TEAM_CTA } from '@/constants/site-copy';
import { cn } from '@/lib/tw-merge/cn';

type CtaSectionProps = {
  headingClassName?: string;
  primaryCta?: {
    label: string;
    to?: string;
    showVideoIcon?: boolean;
    avatar?: {
      src: string;
      alt: string;
    };
  };
};

export default function CTA({ headingClassName, primaryCta }: CtaSectionProps) {
  return (
    <section className="relative flex h-[457px] flex-col items-center justify-center overflow-hidden py-20 text-white sm:h-[553px] sm:py-32">
      {/* Background image */}
      <img
        src={ctaBg}
        alt="Background image"
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover object-center"
      />
      {/* Dim overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[#00000033]"
      />

      <div className="relative z-10 mx-auto w-[90%] max-w-[1056px] text-center text-white">
        <h2
          className={cn(
            'mb-4 font-fraunces text-[32px] font-bold leading-[32px] sm:mb-6 sm:text-5xl sm:leading-[60px] md:text-6xl md:leading-[80px]',
            headingClassName
          )}
        >
          Ready to Activate Your First AI Suite
        </h2>
        <p className="mb-6 text-base leading-6 sm:mb-10 sm:text-xl sm:leading-normal">
          Start with one. Expand on your terms. Watch transformation compound.
        </p>

        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
          <Link
            to={primaryCta?.to ?? ROUTES.SIGNUP}
            className="inline-flex h-[56px] w-full items-center justify-center gap-3 rounded-full bg-primary px-8 py-4 font-medium text-white transition-opacity hover:opacity-90 sm:w-auto sm:text-lg md:h-[64px] md:max-w-[300px]"
          >
            {primaryCta?.avatar && (
              <img
                src={primaryCta.avatar.src}
                alt={primaryCta.avatar.alt}
                className="h-8 w-8 rounded-full bg-[#F0EEDD] object-contain object-top"
              />
            )}
            {primaryCta?.label ?? DEPLOY_AI_TEAM_CTA}
            {primaryCta?.showVideoIcon && (
              <VideoCallIcon
                aria-hidden="true"
                className="text-white/90 h-5 w-5"
              />
            )}
          </Link>
          <Link
            to={ROUTES.CONTACT}
            className="-inline-flex h-[52px] w-full items-center justify-center border-b border-white bg-transparent px-2 py-4 font-medium text-white transition-opacity hover:opacity-80 sm:w-auto sm:text-lg"
          >
            Schedule a Demo
          </Link>
        </div>
      </div>
    </section>
  );
}
